/** Your email settings, in ONE place regardless of how many reps you are looking at.
 *
 *  These were per-rep-panel, which put two different owners on identical checkboxes: a
 *  manager with three reps open saw "Email me the plan" three times, all showing their own
 *  value, next to "Build this plan every week" three times, each a different rep's. Same
 *  styling, opposite meanings.
 *
 *  Everything here is the CALLER's, always fetched and saved without a `rep`.
 */
import React, { useCallback, useEffect, useState } from 'react';
import './SalesPlanEmailSettings.css';
import { MultiSelectDropdown } from '../unified/MultiSelectDropdown';
import { siftApiClient } from '../../api/siftApiClient';
import { describeError } from './salesPlanErrors';
import { emailsSendNothing } from './salesPlanEmailState';
import type { PlanSettings } from '../../api/types';

interface SalesPlanEmailSettingsProps {
  impersonateRole?: string;
  /** Every rep this user may follow, from the same options the CSE Rep filter uses. */
  repOptions?: string[];
}

// Times are the cron's, in ET (SALES_PLAN_BATCH_HOUR / SALES_DIGEST_HOUR). Stated because
// "when does this arrive" is the first thing anyone asks after ticking the box.
const CADENCES = [
  ['weekly_email', 'Email me the plan on Monday',
    'Arrives Monday around 6:00am ET. Delivery only — you can have a plan without the mail.'],
  ['daily_digest', 'Daily digest of what moved',
    'Arrives Tuesday to Friday around 6:30am ET. It only ever suggests — no plan is ever '
    + 'changed for you.'],
] as const;

export const SalesPlanEmailSettings: React.FC<SalesPlanEmailSettingsProps> = ({
  impersonateRole, repOptions,
}) => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<PlanSettings | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Loaded on mount, not on open: the closed header is the only place a manager who ticked
  // the boxes but picked no reps will ever be told that nothing is being sent.
  useEffect(() => {
    if (settings) return;
    const controller = new AbortController();
    siftApiClient.getSalesPlanSettings({ impersonate_role: impersonateRole }, controller.signal)
      .then(setSettings)
      .catch((err) => {
        if (!controller.signal.aborted) setError(describeError(err, 'Could not load these'));
      });
    return () => controller.abort();
  }, [settings, impersonateRole]);

  const save = useCallback(async (key: string, value: unknown) => {
    setSavingKey(key);
    try {
      setSettings(await siftApiClient.setSalesPlanSetting(
        key as 'weekly_email' | 'daily_digest' | 'subscribed_reps',
        value,
        { impersonate_role: impersonateRole },
      ));
    } catch (err) {
      setError(describeError(err, 'Could not save that'));
    } finally {
      setSavingKey(null);
    }
  }, [impersonateRole]);

  const chosen = settings?.subscribed_reps ?? [];
  const wantsMail = Boolean(settings?.weekly_email || settings?.daily_digest);
  const sendsNothing = emailsSendNothing(settings);

  // Open on its own when nothing will be sent, so the rep picker is in front of them rather
  // than behind a header they have no reason to click.
  useEffect(() => {
    if (sendsNothing) setOpen(true);
  }, [sendsNothing]);

  return (
    <section className="spe-root">
      <button
        type="button"
        className="spe-head"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={`spe-caret${open ? ' spe-caret-open' : ''}`}>▸</span>
        <span className="spe-title">My emails</span>
        <span className={`spe-hint${sendsNothing ? ' spe-hint-warn' : ''}`}>
          {settings
            ? sendsNothing
              ? 'Pick reps to get emails'
              : wantsMail
                ? `${chosen.length || 1} rep${chosen.length === 1 || !chosen.length ? '' : 's'}`
                : 'Off'
            : 'Monday plan and daily digest'}
        </span>
      </button>

      {open ? (
        <div className="spe-body">
          {error ? <div className="spe-error">{error}</div> : null}
          {settings ? (
            <>
              {CADENCES.map(([key, label, hint]) => (
                <label className="spe-setting" key={key}>
                  <input
                    type="checkbox"
                    checked={Boolean(settings[key])}
                    disabled={savingKey === key}
                    onChange={(e) => save(key, e.target.checked)}
                  />
                  <span>
                    <strong>{label}</strong>
                    <em>{hint}</em>
                  </span>
                </label>
              ))}

              {wantsMail ? (
                <div className="spe-subs">
                  <strong>Whose weeks to send me</strong>
                  {sendsNothing ? (
                    <p className="spe-warn" role="alert">
                      You don&apos;t have a territory of your own, so nothing will be sent until
                      you pick at least one rep below.
                    </p>
                  ) : null}
                  <em>
                    One email, however many you pick. Leave it empty for just your own
                    territory — pick reps if you manage them, or if you are covering for
                    someone. Several reps arrive as totals and exceptions, not full routes.
                  </em>
                  {repOptions?.length ? (
                    // The same control as the CSE Rep filter above: a flat checkbox list runs
                    // to 200 reps for a director, which is a scroll, not a choice.
                    <MultiSelectDropdown
                      label="Reps"
                      options={repOptions}
                      values={chosen}
                      onChange={(next) => save('subscribed_reps', next)}
                      placeholder="Just my own territory"
                      // The shared control's "all" entry CLEARS the selection, and an empty
                      // selection here means "me". Labelled "All" it would read as the
                      // opposite of what it does.
                      allOptionLabel="Just my own territory"
                      disabled={savingKey === 'subscribed_reps'}
                    />
                  ) : (
                    <p className="spe-empty">
                      No reps in view. Widen the filters above to choose whose weeks to follow.
                    </p>
                  )}
                </div>
              ) : null}
            </>
          ) : error ? null : <p className="spe-empty">Loading…</p>}
        </div>
      ) : null}
    </section>
  );
};
